import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";
import type { ReactNode } from "react";
import { haptic, setHapticsEnabled, type HapticStyle } from "../haptics";
import { playSound, setSoundsEnabled, type SoundStyle } from "../sounds";

type FeedbackPrefs = {
  hapticsEnabled: boolean;
  soundEnabled: boolean;
};

type FeedbackContextValue = FeedbackPrefs & {
  setHapticsEnabled: (on: boolean) => void;
  setSoundEnabled: (on: boolean) => void;
  /** Fire haptic + optional sound in one call */
  feedback: (hapticStyle: HapticStyle, soundStyle?: SoundStyle) => void;
};

const FeedbackContext = createContext<FeedbackContextValue>({
  hapticsEnabled: true,
  soundEnabled: false,
  setHapticsEnabled: () => {},
  setSoundEnabled: () => {},
  feedback: () => {},
});

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const [hapticsEnabled, setHapticsEnabledState] = useState(true);
  const [soundEnabled, setSoundEnabledState] = useState(false);

  const setHapticsEnabledLocal = useCallback((on: boolean) => {
    setHapticsEnabledState(on);
    setHapticsEnabled(on);
  }, []);

  const setSoundEnabledLocal = useCallback((on: boolean) => {
    setSoundEnabledState(on);
    setSoundsEnabled(on);
  }, []);

  const feedback = useCallback(
    (hapticStyle: HapticStyle, soundStyle?: SoundStyle) => {
      if (hapticsEnabled) haptic(hapticStyle);
      if (soundEnabled && soundStyle) playSound(soundStyle);
    },
    [hapticsEnabled, soundEnabled],
  );

  const value = useMemo(
    () => ({
      hapticsEnabled,
      soundEnabled,
      setHapticsEnabled: setHapticsEnabledLocal,
      setSoundEnabled: setSoundEnabledLocal,
      feedback,
    }),
    [
      hapticsEnabled,
      soundEnabled,
      setHapticsEnabledLocal,
      setSoundEnabledLocal,
      feedback,
    ],
  );

  return (
    <FeedbackContext.Provider value={value}>
      {children}
    </FeedbackContext.Provider>
  );
}

export function useFeedback() {
  return useContext(FeedbackContext);
}
